import apiClient from "./api";

/** What accepting a suggestion creates or changes. Mirrors `SuggestionKind` on the backend. */
export type SuggestionKind =
  | "Shortcut"
  | "Skill"
  | "SkillAmendment"
  | "Toolkit"
  | "Kb"
  | "Schedule"
  | "Defect";

export const SUGGESTION_KINDS: SuggestionKind[] = [
  "Shortcut",
  "Skill",
  "SkillAmendment",
  "Toolkit",
  "Kb",
  "Schedule",
  "Defect",
];

export type SuggestionStatus = "Pending" | "Accepted" | "Dismissed";

/** Which population a suggestion was detected across. Only shortcuts are ever customer-company scoped. */
export type SuggestionLevel = "CompanyWide" | "CustomerCompany";

/** Signal counts and scrubbed quotes behind a suggestion (`evidence`, and `outcome` after acceptance). */
export interface SuggestionEvidence {
  sessions: number;
  distinctUsers: number;
  refusals: number;
  retries: number;
  corrections: number;
  thumbsDown: number;
  approvalsRejected: number;
  toolFailures: number;
  emptyLoops: number;
  humanRequests: number;
  abandoned: number;
  score: number;
  quotes: string[];
  sessionIds: string[];
  tools: string[];
  refusalCategory?: string | null;
  language?: string | null;
}

/** Kind-specific payload. Fields present depend on `kind`; all optional on the wire. */
export interface SuggestionDraft {
  widgetId?: string;
  /** The need's label — after a merge the capability ("ticket search"), shown as the block heading. */
  intent?: string;
  reason?: string;
  // Skill / SkillAmendment
  targetSkillId?: string | null;
  targetSkillName?: string | null;
  name?: string | null;
  category?: string | null;
  humanReadableDescription?: string | null;
  triggerContext?: string | null;
  skillPrompt?: string | null;
  // Toolkit / Defect
  toolId?: string | null;
  toolName?: string | null;
  toolDisplayName?: string | null;
  change?: "enable" | "remove_approval" | "check_schema" | null;
  emptyLoops?: number;
  toolFailures?: number;
  // Kb
  topic?: string;
  questions?: string[];
  // Schedule
  dayOfWeek?: number;
  timeOfDay?: string;
  userId?: string;
  tools?: string[];
}

export interface Suggestion {
  id: string;
  suggestionHash: string;
  /** Groups the kinds proposed for the same need. */
  clusterHash: string;
  kind: SuggestionKind;
  level: SuggestionLevel;
  customerCompanyRefId?: string | null;
  customerCompanyName?: string | null;
  title: string;
  /** Shortcut kind only. */
  promptBody: string;
  description?: string | null;
  icon?: string | null;
  rationale: string;
  occurrenceCount: number;
  distinctCustomerCount: number;
  score: number;
  firstOccurred: string;
  lastOccurred: string;
  representativeQuestions: string[];
  evidence?: SuggestionEvidence | null;
  draft?: SuggestionDraft | null;
  outcome?: SuggestionEvidence | null;
  targetSkillId?: string | null;
  targetSkillName?: string | null;
  /** SkillAmendment only: the target skill's instructions as they are now, for the diff preview. */
  targetSkillPrompt?: string | null;
  targetToolId?: string | null;
  targetToolName?: string | null;
  /** A Shortcut that works best once this Skill suggestion is accepted. */
  dependsOnSuggestionId?: string | null;
  createdSkillId?: string | null;
  createdPromptId?: string | null;
  status: SuggestionStatus;
  createdAt: string;
  decidedAt?: string | null;
}

/** Optional edits made before accepting. Omitted fields keep the drafted values. */
export interface AcceptSuggestionRequest {
  title?: string;
  promptBody?: string;
  description?: string;
  icon?: string;
  skillName?: string;
  skillCategory?: string;
  skillDescription?: string;
  triggerContext?: string;
  skillPrompt?: string;
  /** Skill kind: also assign the new skill to the widget the need was found on. Default true. */
  assignToWidget?: boolean;
  /** Defect kind: switch the tool off as part of accepting. */
  disableTool?: boolean;
}

/** Friction events per session — the number the outcome compares before/after acceptance. */
export const frictionPerSession = (e?: SuggestionEvidence | null): number =>
  !e || e.sessions === 0
    ? 0
    : (e.refusals +
        e.retries +
        e.corrections +
        e.thumbsDown +
        e.approvalsRejected +
        e.toolFailures) /
      e.sessions;

export const suggestionService = {
  /** Lists suggestions, optionally by kind; defaults to Pending — what still needs a decision. */
  list: async (
    kind?: SuggestionKind,
    status: SuggestionStatus = "Pending",
  ): Promise<Suggestion[]> => {
    const response = await apiClient.get("/suggestions", {
      params: { ...(kind ? { kind } : {}), status },
    });
    return response.data;
  },

  /** Pending suggestions of every kind — drives the topbar banner and menu badge. */
  pendingCount: async (): Promise<number> => {
    const response = await apiClient.get("/suggestions/pending-count");
    return response.data?.count ?? 0;
  },

  /**
   * Accepts a suggestion. Any field omitted keeps the drafted value. Throws with a 409 when a
   * shortcut or skill of the resulting name already exists.
   */
  accept: async (
    id: string,
    body?: AcceptSuggestionRequest,
  ): Promise<Suggestion> => {
    const response = await apiClient.post(
      `/suggestions/${id}/accept`,
      body ?? {},
    );
    return response.data;
  },

  /** Dismisses a suggestion. Retained, so `restore` can undo it. */
  dismiss: async (id: string): Promise<Suggestion> => {
    const response = await apiClient.post(`/suggestions/${id}/dismiss`);
    return response.data;
  },

  /** Returns a decided suggestion to the pending list. The undo for dismiss. */
  restore: async (id: string): Promise<Suggestion> => {
    const response = await apiClient.post(`/suggestions/${id}/restore`);
    return response.data;
  },
};
